"use client";

// Remise du compte pro, appliquee sans saisie (GT-261007-HVGQ).
//
// L'admin rattache un code a un ou plusieurs comptes pro, eventuellement limite a des
// destinations (section Discount). GET /api/me/account-discounts rend les codes encore
// utilisables de CE compte. Le panier applique le meilleur d'entre eux par le circuit
// d'un code saisi (`checkDiscountCode`) : meme verification serveur, meme calcul du total,
// meme `discountUuid` envoye au paiement — rien de plus n'est cru cote client. La fiche
// produit annonce la remise qui s'appliquera a sa destination.
import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/app/lib/reducers/api";
import { useAppDispatch, useAppSelector } from "@/app/lib/hooks";
import { checkDiscountCode, resetDiscount } from "@/app/lib/reducers/ordersReducer";

export interface AccountDiscount {
  uuid: string;
  name: string;
  value: number;
  type: "percentage" | "flat";
  allowedCityIds: number[];
}

// Une lecture par compte et par chargement de page : la fiche, le tiroir panier et la
// page de paiement lisent la meme reponse.
const cache = new Map<string, Promise<AccountDiscount[]>>();

const loadFor = (userUuid: string) => {
  if (!cache.has(userUuid)) {
    cache.set(
      userUuid,
      api
        .get("/api/me/account-discounts")
        .then((res) => (Array.isArray(res.data?.discounts) ? (res.data.discounts as AccountDiscount[]) : []))
        .catch(() => {
          cache.delete(userUuid);
          return [];
        })
    );
  }
  return cache.get(userUuid)!;
};

/** Les remises du compte connecte ; vide pour un visiteur ou un compte non pro. */
export function useAccountDiscounts(): AccountDiscount[] {
  const user = useAppSelector((s: any) => s.auth?.user);
  const userUuid: string | undefined = user?.uuid;
  const isPro = user?.isPro === true;
  const [discounts, setDiscounts] = useState<AccountDiscount[]>([]);

  useEffect(() => {
    if (!userUuid || !isPro) {
      setDiscounts([]);
      return undefined;
    }
    let alive = true;
    loadFor(userUuid).then((list) => {
      if (alive) setDiscounts(list);
    });
    return () => {
      alive = false;
    };
  }, [userUuid, isPro]);

  return discounts;
}

const appliesTo = (d: AccountDiscount, cityId: number | null) =>
  !d.allowedCityIds?.length || (cityId != null && d.allowedCityIds.includes(Number(cityId)));

/** La remise qui vaudra pour une destination (fiche produit) : pourcentage le plus fort d'abord. */
export function accountDiscountForCity(discounts: AccountDiscount[], cityId: number | null): AccountDiscount | null {
  const eligible = discounts.filter((d) => appliesTo(d, cityId));
  if (!eligible.length) return null;
  const pct = eligible.filter((d) => d.type === "percentage").sort((a, b) => b.value - a.value);
  return pct[0] || eligible.sort((a, b) => b.value - a.value)[0];
}

/** Ville d'une ligne de panier, quelle que soit la forme de la ligne. */
export const cartLineCityId = (item: any): number | null => {
  const candidates = [item?.tour?.cityId, item?.tour?.city?.id, item?.tour?.city?.cityId, item?.cityId, item?.city?.id];
  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null) continue;
    const num = typeof candidate === "string" ? parseInt(candidate, 10) : candidate;
    if (!Number.isNaN(num)) return num;
  }
  return null;
};

/**
 * La remise qui fait economiser le plus sur ce panier : `lines` = { cityId, amount } en EUR.
 * Memes regles que le serveur (computeServerOwedEUR) : un code limite a des villes ne
 * porte que sur leurs lignes, un montant fixe ne depasse pas ces lignes.
 */
export function bestAccountDiscountForCart(
  discounts: AccountDiscount[],
  lines: { cityId: number | null; amount: number }[]
): AccountDiscount | null {
  let best: AccountDiscount | null = null;
  let bestSaving = 0;
  for (const d of discounts) {
    const eligible = lines.reduce((sum, l) => (appliesTo(d, l.cityId) ? sum + (Number(l.amount) || 0) : sum), 0);
    if (eligible <= 0) continue;
    const saving = d.type === "percentage" ? eligible * (Number(d.value) / 100) : Math.min(Number(d.value), eligible);
    if (saving > bestSaving) {
      best = d;
      bestSaving = saving;
    }
  }
  return best;
}

/**
 * Applique la remise du compte au panier, une fois, si aucun code n'est deja applique.
 * `lines` : lignes du panier (ville + montant EUR). Rend la remise du compte actuellement
 * appliquee (pour l'annoncer), ou null.
 */
export function useAutoAccountDiscount(lines: { cityId: number | null; amount: number }[]): AccountDiscount | null {
  const dispatch = useAppDispatch();
  const discounts = useAccountDiscounts();
  const discountValid = useAppSelector((s: any) => s.orders?.discountValid ?? null);
  const discountUuid = useAppSelector((s: any) => s.orders?.discountUuid ?? null);
  const loading = useAppSelector((s: any) => s.orders?.loading === true);
  const tried = useRef<Set<string>>(new Set());

  const linesKey = lines.map((l) => `${l.cityId}:${Number(l.amount).toFixed(2)}`).join("|");
  const best = useMemo(
    () => bestAccountDiscountForCart(discounts, lines),
    // `linesKey` resume les lignes : le tableau change d'identite a chaque rendu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [discounts, linesKey]
  );

  useEffect(() => {
    if (!best || loading) return;
    // Un code est deja applique (celui du compte, ou un code saisi) : on n'y touche pas.
    if (discountValid === true) return;
    // Une seule tentative par code et par contenu de panier : refuse par le serveur, ou
    // retire a la main, il ne revient qu'apres un changement du panier (le tiroir panier
    // retire le code quand on supprime une ligne).
    const attempt = `${best.uuid}|${linesKey}`;
    if (tried.current.has(attempt)) return;
    tried.current.add(attempt);
    dispatch(resetDiscount());
    dispatch(checkDiscountCode(best.name) as any);
  }, [best, discountValid, loading, dispatch, linesKey]);

  if (discountValid !== true || !discountUuid) return null;
  return discounts.find((d) => d.uuid === discountUuid) || null;
}
